import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';

// Define a more generic record type with just an id property required
export interface BaseRecord {
  id: string;
  [key: string]: any; // Allow any other properties
}

export interface SelectedGridRecordsState {
  // Selected records as a simple array
  selectedRecords: BaseRecord[];
}

const initialState: SelectedGridRecordsState = {
  selectedRecords: [],
};

const selectedGridRecordsSlice = createSlice({
  name: 'selectedGridRecords',
  initialState,
  reducers: {
    // Add a single record to selection

    addSelectedRecord(
      state,
      action: PayloadAction<BaseRecord>
    ) {
      const record = action.payload;
      
      // Check if record already exists based on id to avoid duplicates
      const recordExists = state.selectedRecords.some(
        (r) => r.id === record.id
      );
      
      if (!recordExists) {
        state.selectedRecords.push(record);
      }
    },

    // Remove a record from selection by ID
    removeSelectedRecord(
      state,
      action: PayloadAction<string | number>
    ) {
      const id = action.payload;
      state.selectedRecords = state.selectedRecords.filter(r => r.id !== id);
    },

    // Set multiple selected records at once (replaces existing)
    setSelectedRecords(
      state,
      action: PayloadAction<BaseRecord[]>
    ) {
      state.selectedRecords = action.payload;
    },

    // Clear all selected records
    clearSelectedRecords(
      state
    ) {
      state.selectedRecords = [];
    },
  },
});

// Export actions
export const {
  addSelectedRecord,
  removeSelectedRecord,
  setSelectedRecords,
  clearSelectedRecords,
} = selectedGridRecordsSlice.actions;

// Export selectors
export const selectSelectedRecords = (state: RootState) => 
  state.selectedGridRecords.selectedRecords;

// Selector to get all record ids
export const selectRecordIds = createSelector(
  (state: RootState) => state.selectedGridRecords.selectedRecords,
  (selectedRecords) => selectedRecords.map((record: BaseRecord) => record.id)
);

// Check if a specific record is selected
export const selectIsRecordSelected = createSelector(
  [
    (state: RootState) => state.selectedGridRecords.selectedRecords,
    (_: RootState, recordId: string | number) => recordId
  ],
  (selectedRecords, recordId) => selectedRecords.some((record: BaseRecord) => record.id === recordId)
);

// Filter records by a property value
export const selectRecordsByProperty = createSelector(
  [
    (state: RootState) => state.selectedGridRecords.selectedRecords,
    (_: RootState, _property: string, _value: any) => _property,
    (_: RootState, __: string, value: any) => value
  ],
  (selectedRecords, property, value) => selectedRecords.filter((record: BaseRecord) => record[property] === value)
);


export default selectedGridRecordsSlice.reducer;